import { TemplateElement, defineElement, html } from '@webtides/element-js';
import { windowStore } from '../../../js/store/WindowStore';
import ImageMap from './tessa-image.map';

export const getSrcByDimension = ({ url, width, height }) => {
    let closesDimensions = ImageMap.find(([a, b]) => a > width || b > height) || ImageMap[ImageMap.length - 1];
    return url.replace('{{width}}x{{height}}', closesDimensions.join('x'));
};

export default class TessaImage extends TemplateElement {
    resizeObserver;

    properties() {
        return {
            url: '',
            alt: '',
            lazy: true,
            loading: true,
            error: false,
            resetImage: false,
            imgCssClass: '',
            windowStore,
            width: undefined,
            height: undefined,
            observeSize: false,
            src: '',
            cover: false,
            contain: false,
            scaleDown: true,
        };
    }

    connected() {
        this.updateImageSrc();
        if (this.observeSize) {
            this.resizeObserver = new ResizeObserver(() => {
                this.updateImageSrc();
            });
            this.resizeObserver.observe(this);
        }
    }

    disconnected() {
        this.resizeObserver?.unobserve(this);
    }

    events() {
        return {
            img: {
                load: () => {
                    this.loading = false;
                },
                error: () => {
                    if (this.loading === false) {
                        this.error = true;
                    }
                },
            },
        };
    }

    watch() {
        return {
            url: async () => {
                // nuke image src from DOM, to prevent async rendering of headline and image.
                this.resetImage = true;
                this.loading = true;
                this.error = false;
                this.updateImageSrc();
                await this.requestUpdate();
                this.resetImage = false;
            },
            windowStore: () => this.updateImageSrc(),
        };
    }

    get imgCSSClasses() {
        const fitClasses = {
            'object-cover': this.cover,
            'object-contain': this.contain,
            'object-scale-down': this.scaleDown,
        };
        return Object.entries(fitClasses)
            .filter(([key, value]) => value === true)
            .map(([key]) => key)
            .join(' ');
    }

    get fallBackImage() {
        return window.siteConfig().fallBackImage;
    }

    renderFallbackImage() {
        return html`
            <img
                data-fallback="true"
                class="absolute top-0 inset-x-0"
                alt="tessa-image-fallback"
                src="${this.fallBackImage}"
            />
        `;
    }

    updateImageSrc() {
        const width = this.width ? this.width : this.offsetWidth;
        const height = this.height ? this.height : this.offsetHeight;
        this.src = getSrcByDimension({ url: this.url, width, height });
    }

    renderLoadingImage() {
        return html` <loading-spinner class="absolute inset-0 items-center justify-center"></loading-spinner>`;
    }

    template() {
        if (this.resetImage) return html``;
        if (this.error) {
            return this.renderFallbackImage();
        } else {
            return html`
                <img
                    ref="image"
                    loading="${this.lazy ? 'lazy' : 'eager'}"
                    class="${this.imgCSSClasses} ${this.imgCssClass}"
                    src="${this.src}"
                    alt="${this.alt}"
                />
                ${this.loading ? this.renderLoadingImage() : ''}
            `;
        }
    }
}
defineElement('tessa-image', TessaImage);
